import { useCallback, useEffect, useRef, useState } from 'react';
import type { CanvasMode, Diagram, MermaidErrorInfo, Theme } from '../../store/types.ts';

type CanvasPanelProps = {
  diagram: Diagram | undefined;
  canvasMode: CanvasMode;
  onCanvasModeChange: (mode: CanvasMode) => void;
  renderedSvg: string;
  renderError: MermaidErrorInfo | null;
  isRendering: boolean;
  zoom: number;
  pan: { x: number; y: number };
  isFullscreen: boolean;
  spaceHeld: boolean;
  isSending: boolean;
  viewportRef: React.RefObject<HTMLDivElement | null>;
  stageRef: React.RefObject<HTMLDivElement | null>;
  canvasShellRef: React.RefObject<HTMLDivElement | null>;
  onWheel: (event: React.WheelEvent<HTMLDivElement>) => void;
  onDragStart: (event: React.MouseEvent<HTMLDivElement>) => void;
  onDragMove: (event: React.MouseEvent<HTMLDivElement>) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetView: () => void;
  onFitToView: () => void;
  onToggleFullscreen: () => void;
  onSetTheme: (theme: Theme) => void;
  onExportSvg: () => void;
  onExportPng1x: () => void;
  onExportPng2x: () => void;
  onAutoFix: () => void;
  onSwitchToCode: () => void;
  onToggleProperties: () => void;
  excalidrawSlot?: React.ReactNode;
};

export function CanvasPanel({
  diagram,
  canvasMode,
  onCanvasModeChange,
  renderedSvg,
  renderError,
  isRendering,
  zoom,
  pan,
  isFullscreen,
  spaceHeld,
  isSending,
  viewportRef,
  stageRef,
  canvasShellRef,
  onWheel,
  onDragStart,
  onDragMove,
  onZoomIn,
  onZoomOut,
  onResetView,
  onFitToView,
  onToggleFullscreen,
  onSetTheme,
  onExportSvg,
  onExportPng1x,
  onExportPng2x,
  onAutoFix,
  onSwitchToCode,
  onToggleProperties,
  excalidrawSlot,
}: CanvasPanelProps) {
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  const hasDiagram = Boolean(diagram?.mermaidCode.trim() && renderedSvg && !renderError);

  // Close export dropdown on outside click or Escape
  const closeExport = useCallback(() => setExportOpen(false), []);
  useEffect(() => {
    if (!exportOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) closeExport();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeExport();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [exportOpen, closeExport]);

  return (
    <div className="ds-canvas-panel" ref={canvasShellRef}>
      {/* Canvas toolbar */}
      <div className="ds-canvas__toolbar">
        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: '2px' }}>
          <button
            type="button"
            className={`ds-btn ds-btn--sm ${canvasMode === 'mermaid' ? 'ds-btn--primary' : 'ds-btn--ghost'}`}
            onClick={() => onCanvasModeChange('mermaid')}
          >
            Mermaid
          </button>
          <button
            type="button"
            className={`ds-btn ds-btn--sm ${canvasMode === 'draw' ? 'ds-btn--primary' : 'ds-btn--ghost'}`}
            onClick={() => onCanvasModeChange('draw')}
          >
            Draw
          </button>
        </div>

        <div className="ds-canvas__toolbar-actions">
          <button type="button" className="ds-btn ds-btn--sm ds-btn--ghost" onClick={onToggleProperties} title="Diagram properties (I)">
            Info
          </button>
          <button type="button" className="ds-btn ds-btn--sm ds-btn--ghost" onClick={onToggleFullscreen}>
            {isFullscreen ? 'Exit' : 'Fullscreen'}
          </button>
        </div>
      </div>

      {/* Canvas area */}
      {canvasMode === 'mermaid' ? (
        <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
          <div
            className={`ds-canvas__viewport ${diagram?.theme === 'dark' ? 'ds-canvas__viewport--dark' : ''}`}
            ref={viewportRef}
            onWheel={onWheel}
            onMouseDown={onDragStart}
            onMouseMove={onDragMove}
            style={{ width: '100%', height: '100%', cursor: spaceHeld ? 'grab' : undefined }}
          >
            {!diagram?.mermaidCode.trim() && (
              <div className="ds-canvas__empty">
                <h3>No diagram yet</h3>
                <p>Use the Chat tab to generate a diagram, or switch to Code and write Mermaid directly.</p>
              </div>
            )}

            {isRendering && <div className="ds-canvas__loading">Rendering diagram...</div>}

            {renderError && (
              <div className="ds-canvas__error">
                <h3>Mermaid Parse Error</h3>
                <p>
                  {renderError.line ? `Line ${renderError.line}` : 'Line unknown'}
                  {renderError.column ? `, Column ${renderError.column}` : ''}
                </p>
                <pre>{renderError.raw}</pre>
                <div className="ds-canvas__error-actions">
                  <button type="button" className="ds-btn ds-btn--sm" onClick={onSwitchToCode}>
                    Open Code
                  </button>
                  <button
                    type="button"
                    className="ds-btn ds-btn--sm ds-btn--primary"
                    onClick={onAutoFix}
                    disabled={isSending}
                  >
                    Auto-fix with Claude
                  </button>
                </div>
              </div>
            )}

            {!renderError && renderedSvg && (
              <div
                className="ds-canvas__stage"
                ref={stageRef}
                style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
                dangerouslySetInnerHTML={{ __html: renderedSvg }}
              />
            )}
          </div>

          {/* Floating contextual toolbar — only visible when a diagram is rendered */}
          {hasDiagram && (
            <div className="ds-float-toolbar">
              <button
                type="button"
                className={`ds-float-toolbar__btn ${diagram?.theme === 'light' ? 'ds-float-toolbar__btn--active' : ''}`}
                onClick={() => onSetTheme('light')}
                title="Light theme"
              >
                Light
              </button>
              <button
                type="button"
                className={`ds-float-toolbar__btn ${diagram?.theme === 'dark' ? 'ds-float-toolbar__btn--active' : ''}`}
                onClick={() => onSetTheme('dark')}
                title="Dark theme"
              >
                Dark
              </button>
              <div className="ds-float-toolbar__sep" />
              <button type="button" className="ds-float-toolbar__btn" onClick={onFitToView} title="Fit to view">
                Fit
              </button>
              <div className="ds-float-toolbar__sep" />
              <div ref={exportRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="ds-float-toolbar__btn"
                  onClick={() => setExportOpen((v) => !v)}
                  title="Export"
                >
                  Export
                </button>
                {exportOpen && (
                  <div className="ds-float-toolbar__menu">
                    <button type="button" className="ds-float-toolbar__menu-item" onClick={() => { onExportSvg(); setExportOpen(false); }}>
                      SVG
                    </button>
                    <button type="button" className="ds-float-toolbar__menu-item" onClick={() => { onExportPng1x(); setExportOpen(false); }}>
                      PNG 1x
                    </button>
                    <button type="button" className="ds-float-toolbar__menu-item" onClick={() => { onExportPng2x(); setExportOpen(false); }}>
                      PNG 2x
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Floating zoom controls */}
          <div className="ds-zoom">
            <button type="button" className="ds-btn ds-btn--sm ds-btn--ghost" onClick={onZoomOut}>
              -
            </button>
            <span className="ds-zoom__label">{Math.round(zoom * 100)}%</span>
            <button type="button" className="ds-btn ds-btn--sm ds-btn--ghost" onClick={onZoomIn}>
              +
            </button>
            <button type="button" className="ds-btn ds-btn--sm ds-btn--ghost" onClick={onResetView}>
              Reset
            </button>
            <button type="button" className="ds-btn ds-btn--sm ds-btn--ghost" onClick={onFitToView}>
              Fit
            </button>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
          {excalidrawSlot || (
            <div className="ds-canvas__empty" style={{ position: 'relative', marginTop: 80 }}>
              <h3>Excalidraw Whiteboard</h3>
              <p>Draw mode coming soon. Install @excalidraw/excalidraw to enable.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
